import { createSelector, createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const productsAdapter = createEntityAdapter({
  sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
});

const initialState = productsAdapter.getInitialState();

export const productsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query({
      query: () => "/product",
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
      transformResponse: (responseData) => {
        const loadedProducts = responseData.map((product) => {
          product.id = product._id;
          return product;
        });
        return productsAdapter.setAll(initialState, loadedProducts);
      },
      providesTags: (result, error, arg) => {
        if (result?.ids) {
          return [
            { type: "Product", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Product", id })),
          ];
        } else return [{ type: "Product", id: "LIST" }];
      },
    }),
    // getPaginatedProducts: builder.query({
    //   query: ({ page, need }) => `/product/paginatedProducts?page=${page}&need=${need}`,
    // }),
    getPaginatedProducts: builder.query({
      query: ({ page, need, userId, categoryId, all }) => {
        let url = `/product/paginatedProducts?page=${page}&need=${need}&userId=${userId}&all=${all}/`;
        if (categoryId) {
          url += `&categoryId=${categoryId}`;
        }
        console.log('API URL:', url);
        return url;
      },
      providesTags: (result, error, { page, need, userId, categoryId, all }) => [
        { type: 'Products', page, need, userId, categoryId, all }
      ],
      // Force refetch when any of these parameters change
      refetchOnMountOrArgChange: true
    }),
    // prefetchProducts: builder.query({
    //   query: ({ page, need }) => `/product/paginatedProducts?page=${page}&need=${need}`,
    // }),
    getProductById: builder.query({
      query: (id) => `/product/getProductById?id=${id}`,
    }),
    getCartProducts: builder.query({
      query: (id) => `/product/myCart?id=${id}`,
      providesTags: (result, error, arg) => [{ type: 'Product', user: arg.id }],
    }),
    getMyProducts: builder.query({
      query: (id) => `/product/myStore?id=${id}`,
      // providesTags: (result, error, arg) => [{ type: 'Product', user: arg.id }],
      // providesTags: (result, error, {id}) => [
      //   { type: 'User', id: id },
      //   { type: 'Product', id: 'LIST' }
      // ],
      providesTags: ["product"]
    }),
    getMyProductIds: builder.query({
      query: (id) => `/product/myProductIds?id=${id}`,
      providesTags: (result, error, arg) => [{ type: 'Product', id: arg.id }],
    }),
    // ---------------------------------------------------------------------------------
    getSearchProducts: builder.query({
      query: ({ query, page }) => `/product/getSearchResult?q=${query}&page=${page}`,
      providesTags: (result, error, { query, page }) => 
        result 
          ? [
              ...result.results.map(({ _id }) => ({ type: 'Product', id: _id })),
              { type: 'Product', id: `PARTIAL-LIST-${query}-${page}` },
            ]
          : [{ type: 'Product', id: `PARTIAL-LIST-${query}-${page}` }],
    }),
    getSimilarProducts: builder.query({
      query: (id) => `/product/similar?id=${id}`,
      providesTags: (result, error, id) => [{ type: 'Product', id }]
    }),
    // getSearchProducts: builder.query({
    //   query: ({ query, page }) => `/product/getSearchResult?q=${query}&page=${page}`,
    //   providesTags: (result, error, { query, page }) => 
    //     result 
    //   ? [
    //     ...result.results.map(({ _id }) => ({ type: 'Product', id: _id })),
    //     { type: 'Product', id: 'PARTIAL-LIST' },
    //   ]
    //   : [{ type: 'Product', id: 'PARTIAL-LIST' }],
    // }),
    // getSearchProducts: builder.query({
    //   query: ({ query, page }) => `/product/getSearchResult?q=${query}&page=${page}`,
    //   providesTags: (result, error, { query, page }) => 
    //     result 
    //       ? [
    //           ...result.results.map(({ _id }) => ({ type: 'Product', id: _id })),
    //           { type: 'Product', id: `PARTIAL-LIST-${query}-${page}` },
    //         ]
    //       : [{ type: 'Product', id: `PARTIAL-LIST-${query}-${page}` }],
    // }),
    // ---------------------------------------------------------------------------------
    getMyNewestProduct: builder.query({
      query: (id) => `/product/myNewestProduct?id=${id}`,
      // providesTags: (result, error, arg) => [{ type: 'Product', user: arg.id }],
    }),
    addNewProduct: builder.mutation({
      query: data => ({
        url: "/product/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Product"]
    }),
    updateProduct: builder.mutation({
      query: data => ({
        url: "/product/edit",
        method: "PATCH",
        body: data,
      }),
      // invalidatesTags: (result, error, arg) => [
      //   { type: "Product", id: arg.id }
      // ]
      invalidatesTags: ["Product"]
    }),
    deleteProduct: builder.mutation({
      query: ({ id }) => ({
          url: `/product/delete`,
          method: "DELETE",
          body: { id }
      }),
      invalidatesTags: (result, error, arg) => [
          { type: "Product", id: arg.id }
      ]
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetPaginatedProductsQuery,
  useGetProductByIdQuery,
  useGetCartProductsQuery,
  useGetMyProductsQuery,
  useGetMyProductIdsQuery,
  useGetSearchProductsQuery,
  useGetSimilarProductsQuery,
  useGetMyNewestProductQuery,
  useAddNewProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  // prefetchProducts,
  usePrefetch,
} = productsApiSlice

// returns the query result object
export const selectProductsResult = productsApiSlice.endpoints.getProducts.select()

// creates memoized selector
const selectProductsData = createSelector(
  selectProductsResult,
  productsResult => productsResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
  selectAll: selectAllProducts,
  selectById: selectProductById,
  selectIds: selectProductIds
  // Pass in a selector that returns the products slice of state
} = productsAdapter.getSelectors(state => selectProductsData(state) ?? initialState)